import {ChangeEvent, FC, RefObject, useRef} from "react"
import {ReactSVG} from "react-svg"

import {InputField} from "@frontend/components"
import {IconButton} from "@frontend/components/ui/button/icon-button"
import addIcon from "@frontend/design/icons/add.svg"
import arrowUpIcon from "@frontend/design/icons/arrow-up.svg"
import closeIcon from "@frontend/design/icons/close.svg"
import stopIcon from "@frontend/design/icons/stop.svg"
import {useTranslation} from "@frontend/i18n"
import {MessageImage} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {hstack} from "@styled-system/patterns"

// Keep in sync with app/src/app/api/addHumanMessageToCase/route.ts
const MAX_MESSAGE_LENGTH = 3000

interface Props {
  value: string
  images?: MessageImage[]
  onFocus?: () => void
  onChange: (value: string) => void
  onImagesChange?: (images: MessageImage[]) => void
  onSend: () => void
  onAbort: () => void
  disabled?: boolean
  isReceiving?: boolean
  inputRef?: RefObject<HTMLTextAreaElement | null>
  className?: string
  isFirstMessage?: boolean
}

export const ConversationInputField: FC<Props> = ({
  value,
  images = [],
  onFocus,
  onChange,
  onImagesChange,
  onSend,
  onAbort,
  disabled,
  isReceiving,
  inputRef,
  className,
  isFirstMessage,
}) => {
  const t = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const placeholder = t(
    isFirstMessage ? "case.inputInitialPlaceholder" : "case.inputPlaceholder",
  )

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)

    try {
      const filePromises = fileArray.map(
        (file) =>
          new Promise<MessageImage>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              if (e.target?.result && typeof e.target.result === "string") {
                resolve({
                  url: e.target.result,
                  title: file.name,
                })
              } else {
                reject(new Error("Failed to read file"))
              }
            }
            reader.onerror = () => reject(new Error("File reading error"))
            reader.readAsDataURL(file)
          }),
      )

      const newImages = await Promise.all(filePromises)

      // Use a functional update if possible, but here we depend on props
      // We create a new array to ensure state change is detected
      const updatedImages = [...images, ...newImages]
      onImagesChange?.(updatedImages)
    } catch (error) {
      console.error("Error reading files", error)
    } finally {
      // Reset file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  const removeImage = (index: number): void => {
    const newImages = [...images]
    newImages.splice(index, 1)
    onImagesChange?.(newImages)
  }

  return (
    <div
      className={css({
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      })}
    >
      {images.length > 0 && (
        <div className={hstack({gap: 2, overflowX: "auto", paddingBottom: 2})}>
          {images.map((image, index) => (
            <div
              key={index}
              className={css({position: "relative", flexShrink: 0})}
            >
              <img
                src={image.url}
                alt={image.title ?? "Uploaded image"}
                className={css({
                  height: 60,
                  width: 60,
                  objectFit: "cover",
                  borderRadius: "md",
                  border: "1px solid token(colors.border.primary)",
                })}
              />
              <button
                onClick={() => removeImage(index)}
                className={css({
                  position: "absolute",
                  top: -5,
                  right: -5,
                  backgroundColor: "background.primary",
                  borderRadius: "full",
                  border: "1px solid token(colors.border.primary)",
                  cursor: "pointer",
                  padding: 1,
                  "& svg": {
                    width: 12,
                    height: 12,
                  },
                })}
              >
                <ReactSVG src={closeIcon} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className={css({width: "100%"})}>
        <input
          type="file"
          accept="image/*"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          className={css({display: "none"})}
        />
        <InputField
          value={value}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={placeholder}
          onFocus={onFocus}
          onChange={onChange}
          onEnter={onSend}
          disabled={disabled}
          inputRef={inputRef}
          leftComponent={
            <IconButton
              variant="unstyled"
              size="small"
              icon={<ReactSVG src={addIcon} />}
              css={css.raw({
                width: 24,
                height: 24,
                flexShrink: 0,
                color: "text.secondary",
                cursor: "pointer",
                "&:hover": {
                  color: "text.primary",
                },
              })}
              ariaLabel={t("conversation.uploadImage")}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
            />
          }
          rightComponent={
            isReceiving ? (
              <IconButton
                onClick={onAbort}
                ariaLabel={t("conversation.abort")}
                variant="unstyled"
                css={css.raw({
                  maxHeight: 29,
                  maxWidth: 20,
                  cursor: "pointer",
                })}
                icon={<ReactSVG src={stopIcon} />}
              />
            ) : (
              <IconButton
                testId="submit-msg"
                variant="tertiaryBrand"
                size="large"
                icon={<ReactSVG src={arrowUpIcon} />}
                css={css.raw({width: 32, height: 32})}
                ariaLabel={t("conversation.send")}
                onClick={onSend}
                disabled={
                  disabled || (value.length === 0 && images.length === 0)
                }
              />
            )
          }
          className={className}
        />
      </div>
    </div>
  )
}
